import { useDispatch } from 'react-redux';

import { Text } from '@opendoor/bricks/core';
import Field from '@opendoor/cinderblocks/growth/novo/inputs/Field';
import {
  RadioGroupInput,
  RadioGroupInputProps,
  RadioInputProps,
} from '@opendoor/cinderblocks/growth/novo/inputs/RadioInput';

import { trackAnswerChange } from 'src/components/fields/trackAnswerChange';
import { getErrorMessage } from 'src/components/flowPage/twoColumn/ErrorMessages';
import FollowUps from 'src/components/novo/fields/FollowUps';
import { getQuestionKeys } from 'src/lib/flow';
import type { RadioGroupQuestion } from 'src/lib/flow/FlowQuestion';
import { checkValidation } from 'src/lib/flow/validation';
import { changeAnswers, SavedAnswer } from 'src/store/sellerInput';

// exported for testing
export const getKeysToClear = (
  options: RadioGroupQuestion['options'],
  newValue: string | boolean | number,
) => {
  const keys: Array<string> = [];
  options.forEach((option) => {
    if (option.followUps && newValue !== option.value) {
      // get keys to remove
      keys.push(...getQuestionKeys(option.followUps));
    }
  });
  return keys;
};

type RadioGroupProps = {
  question: RadioGroupQuestion;
  value?: string;
  showValidation?: boolean;
};

const RadioGroup: React.FC<RadioGroupProps> = ({ question, value, showValidation }) => {
  const { label, helperText, key: name, options } = question;
  const dispatch = useDispatch();

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      let valueToSet: string | boolean = event.target.value;
      if (valueToSet === 'true') {
        valueToSet = true;
      } else if (valueToSet === 'false') {
        valueToSet = false;
      }

      const keys = getKeysToClear(options, valueToSet);

      const newAnswers: Array<{ key: string; answer: SavedAnswer | null }> = [];
      //TODO: replace with a clearAnswers action?
      keys.forEach((key) => {
        newAnswers.push({ key, answer: null });
      });

      newAnswers.push({ key: name, answer: valueToSet });

      dispatch(changeAnswers(newAnswers));
      trackAnswerChange(name, JSON.stringify(valueToSet));
    }
  };

  const validationResult = checkValidation(question, value);

  const radioGroupOptions: Array<RadioInputProps> = (options || [])
    .filter(({ hidden }) => !hidden)
    .map((optionProps) => {
      const checked = optionProps.value == `${value}`;
      return {
        id: `${name}-${optionProps.value}`,
        name,
        value: optionProps.value as string,
        error: showValidation && !validationResult.isValid,
        checked,
        onChange,
        children: (
          <>
            {optionProps.label}
            <Text size="100" color="warmgrey800">
              {optionProps.description}
            </Text>
          </>
        ),
        additionalChildren:
          checked && optionProps.followUps ? (
            <FollowUps questions={optionProps.followUps} />
          ) : undefined,
      };
    });

  if (label) {
    return (
      <Field<RadioGroupInputProps>
        label={label}
        helperText={helperText}
        InputComponent={RadioGroupInput}
        errorText={getErrorMessage(validationResult)}
        input={{
          id: `radio-group-${name}`,
          analyticsName: `reception-fe-question-${name}`,
          error: showValidation && !validationResult.isValid,
          name: name,
          options: radioGroupOptions,
        }}
      />
    );
  }

  return (
    <RadioGroupInput
      id={`radio-group-${name}`}
      analyticsName={`reception-fe-question-${name}`}
      error={showValidation && !validationResult.isValid}
      name={name}
      options={radioGroupOptions}
    />
  );
};

export default RadioGroup;
